import React, { useState, useEffect, useRef } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { useForm } from 'react-hook-form';
import { translateData, getLanguages } from '../lib/api';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast } from 'react-toastify';

const customStyles = `
  .form-grid-container {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 1.5rem !important;
    width: 100% !important;
    margin-bottom: 1.25rem !important;
  }
  .form-control, textarea {
    width: 100% !important;
    border-radius: 8px !important;
    padding: 8px 12px !important;
    border: 1px solid #ced4da !important;
    background: #fff !important;
  }
  .error-message { color: #dc3545; font-size: 0.75rem; }
  .success-message { color: #28a745; text-align: center; margin-bottom: 1rem; }

  .image-upload {
    border: 2px dashed #e2e8f0 !important;
    border-radius: 8px !important;
    padding: 1rem !important;
    text-align: center !important;
    background: #f8fafc !important;
    cursor: pointer !important;
    min-height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .image-upload:hover {
    background: #f1f5f9 !important;
  }

  .upload-icon {
    font-size: 24px;
    color: #64748b;
  }

  .upload-text {
    color: #64748b;
    font-size: 0.875rem;
  }

  .image-preview {
    position: relative;
    margin-top: 10px;
  }

  .image-preview img {
    width: 100%;
    max-height: 200px;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
  }

  .remove-image-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
  }

  .remove-image-btn:hover {
    background: #c82333;
  }

  /* Main Tabs (English / Other Languages) */
  .main-tabs { display: flex; gap: 8px; margin-bottom: 12px; }
  .main-tab {
    padding: 8px 14px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
  }
  .main-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }

  /* Language Sub-Tabs */
  .lang-tabs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
  .lang-tab {
    padding: 6px 10px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
  }
  .lang-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }

  .drawer-content-scroll {
    height: calc(100vh - 40px);
    overflow-y: auto !important;
    padding-right: 10px;
  }

  .file-info {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 4px;
  }
`;

const safeParse = (val) => {
  if (!val) return {};
  if (typeof val === 'object') return val;
  try {
    return JSON.parse(val);
  } catch {
    return {};
  }
};

const parseOtherLangField = (input, fieldKey) => {
  const parsed = safeParse(input);
  const obj = parsed && parsed[fieldKey] ? parsed[fieldKey] : {};
  return typeof obj === 'object' && obj ? obj : {};
};

const isEnglishCode = (code) => String(code || '').toLowerCase() === 'en';

export default function AddSubject({ isOpen, onClose, onSubmit, subjectToEdit }) {
  const [message, setMessage] = useState(null);
  const [step, setStep] = useState('basic'); // 'basic' | 'translate' | 'translations'
  const [mainTab, setMainTab] = useState('english'); // 'english' | 'other'
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState('');
  const [images, setImages] = useState([]);
  const [fileObjects, setFileObjects] = useState([]);

  // Translations state — holds ONLY non-English entries
  const [translations, setTranslations] = useState({
    SubjectName: {}
  });

  const nameInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues
  } = useForm({
    defaultValues: {
      name: subjectToEdit?.SubjectName || '',
      active: subjectToEdit?.IsActive !== undefined ? subjectToEdit.IsActive : true,
    },
  });

  const selectedLangName = languages.find(l => l.GoogleLanguageCode === selectedLang)?.LanguageName || selectedLang || '';

  // ✅ Non-English languages only (English handled in the English tab)
  const otherLanguages = languages.filter(l => !isEnglishCode(l.GoogleLanguageCode));

  // Keep selectedLang valid whenever languages change
  useEffect(() => {
    if (!otherLanguages.length) return;
    if (!otherLanguages.some(l => l.GoogleLanguageCode === selectedLang)) {
      setSelectedLang(otherLanguages[0].GoogleLanguageCode);
    }
  }, [languages]);

  const prefillTranslationsFromEdit = () => {
    if (!subjectToEdit || languages.length === 0) return;

    // Parse JSON strings from BE shape in subjectToEdit
    const tName = parseOtherLangField(subjectToEdit.SubjectNameOtherLang, 'SubjectName');

    const next = { SubjectName: {} };

    // ✅ Only non-English entries; English read from subjectToEdit.SubjectName via the form
    otherLanguages.forEach(({ GoogleLanguageCode }) => {
      next.SubjectName[GoogleLanguageCode] = tName[GoogleLanguageCode] || '';
    });

    setTranslations(next);
  };
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => nameInputRef.current && nameInputRef.current.focus(), 100);
    }

    if (subjectToEdit) {
      setValue('name', subjectToEdit.SubjectName || '');
      setValue('active', subjectToEdit.IsActive !== undefined ? subjectToEdit.IsActive : true);

      // Prefill image if exists
      const loaded = [];
      if (subjectToEdit.UploadedImageName || subjectToEdit.SystemGeneratedImageName) {
        loaded.push({
          id: `existing-${Date.now()}`,
          url: subjectToEdit.SystemGeneratedImageName || subjectToEdit.UploadedImageName,
          name: subjectToEdit.UploadedImageName || 'Existing Image',
          isExisting: true
        });
      }
      setImages(loaded);
      setFileObjects([]);
    } else {
      reset({ name: '', active: true });
      setImages([]);
      setFileObjects([]);
      setTranslations({ SubjectName: {} });
    }

    setStep('basic');
    setMessage(null);
    setMainTab('english');
    setSelectedLang('');

    if (isOpen) {
      getLanguages()
        .then(res => {
          const list = res?.data || res || [];
          setLanguages(list);
          // ✅ Default to first NON-English language
          const firstNonEn = list.find(l => !isEnglishCode(l.GoogleLanguageCode));
          if (firstNonEn) setSelectedLang(firstNonEn.GoogleLanguageCode);
          else if (list.length > 0) setSelectedLang(list[0].GoogleLanguageCode);
        })
        .catch(err => {
          console.error('Failed to load languages:', err);
          setLanguages([]);
          setSelectedLang('');
        });
    }
  }, [isOpen]);

  useEffect(() => {
    if (subjectToEdit && languages.length > 0) {
      prefillTranslationsFromEdit();
      if (!selectedLang && otherLanguages.length > 0) {
        setSelectedLang(otherLanguages[0].GoogleLanguageCode);
      }
    }
  }, [languages]);

  const callTranslateApi = async ({ SubjectName }) => {
    try {
      const res = await translateData({
        SubjectName
      });

      const maybeData = res?.data ?? res ?? {};
      const data = maybeData?.data && (maybeData.SubjectName === undefined)
        ? maybeData.data
        : maybeData;

      const normalized = { SubjectName: {} };

      // ✅ Only non-English languages; English injected at save time from user's input
      otherLanguages.forEach(({ GoogleLanguageCode }) => {
        normalized.SubjectName[GoogleLanguageCode] =
          data.SubjectName?.[GoogleLanguageCode] || data.SubjectName?.en || SubjectName;
      });

      return normalized;
    } catch (err) {
      console.error('Translate API error: ', err);

      const fallback = { SubjectName: {} };
      otherLanguages.forEach(({ GoogleLanguageCode }) => {
        fallback.SubjectName[GoogleLanguageCode] = SubjectName;
      });
      return fallback;
    }
  };

  const onInitialTranslate = async (data) => {
    if (languages.length === 0) {
      toast('Languages not loaded yet. Please try again.');
      return;
    }

    setStep('translate');

    const translated = await callTranslateApi({
      SubjectName: data.name
    });

    console.log('Translated data:', translated);

    setTranslations(prev => ({
      SubjectName: { ...prev.SubjectName, ...translated.SubjectName }
    }));

    setStep('translations');
    setMainTab('other');
  };

  const onFinalSave = async () => {
    const name = getValues('name');
    const active = getValues('active');

    // ✅ English injected from user's input; other langs from translations state
    const subjectNameOtherLang = {
      ...translations.SubjectName,
      en: name,
    };

    const payload = {
      SubjectName: name,
      IsActive: active,
      SubjectNameOtherLang: JSON.stringify({ SubjectName: subjectNameOtherLang }),
      ...(subjectToEdit && subjectToEdit.ID ? { ID: subjectToEdit.ID } : {})
    };

    console.log("Final payload:", payload);
    await onSubmit(payload, !!subjectToEdit, fileObjects[0] || null);

    setMessage({
      type: 'success',
      text: subjectToEdit ? 'Subject updated successfully!' : 'Subject added successfully!'
    });

    setTimeout(() => {
      onClose();
      setMessage(null);
    }, 1200);
  };

  // ✅ Validate non-English only; English comes from English tab
  const isTranslationsValid = () => {
    return otherLanguages.every((lang) => {
      const code = lang.GoogleLanguageCode;
      return translations.SubjectName[code]?.trim();
    });
  };

  /** -------- Image upload logic -------- */
  const handleImageUpload = (event) => {
    const fileList = event.target.files || event.dataTransfer?.files || [];
    if (!fileList.length) return;

    const file = fileList[0];

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast('Please upload a valid image file (JPEG, PNG, GIF, WebP)');
      return;
    }

    const maxSize = 1 * 1024 * 1024;
    if (file.size > maxSize) {
      toast('Image size should be less than 1MB');
      return;
    }

    const imgObj = {
      id: Date.now(),
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      type: file.type
    };

    setImages([imgObj]);
    setFileObjects([file]);
    setMessage(null);
  };

  const handleRemoveImage = () => {
    setImages([]);
    setFileObjects([]);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderImageUpload = () => (
    <div>
      <label className="form-label">
        Upload Image {!subjectToEdit && <span className="text-danger-600">*</span>}
      </label>
      <div
        className="image-upload"
        onClick={() => document.getElementById('subjectImgInput')?.click()}
      >
        <input
          id="subjectImgInput"
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
        <div className="upload-text">
          {images.length === 0 ? 'Click to upload image' : 'Click to change image'}
        </div>
        <div className="upload-text" style={{ fontSize: '0.75rem' }}>
          Supports: JPEG, PNG, GIF, WebP
        </div>
      </div>
        <small>Image size should be less than 1MB</small>

      {images.length > 0 && (
        <div className="image-preview">
          <img
            src={images[0].isExisting ?
              `${process.env.REACT_APP_API_BASE_URL || 'https://api.soksan-dss.com'}/uploads/${images[0].url}` :
              images[0].url
            }
            alt="Subject"
          />
          <button
            type="button"
            className="remove-image-btn"
            onClick={handleRemoveImage}
          >
            ×
          </button>
          <div className="file-info">
            {images[0].name}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <style>{customStyles}</style>

      <Drawer open={isOpen} direction="right" size={700} className="p-24">
        <button
                    variant="outline"
                    onClick={onClose}
                    style={{ border: 'none', color: '#000', padding: 0, textAlign: 'end', padding: '15px', position: 'absolute', top: 0, right: 0 }}
                  >
                    <Icon icon="lucide:x" className="text-2xl" />
                  </button>
        <div className="card-body drawer-content-scroll">
          <h6 className="text-xxl text-primary-light mb-16">
            {subjectToEdit ? 'Edit Subject' : 'Add New Subject'}
          </h6>

          {message && (
            <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
              {message.text}
            </div>
          )}

          {/* Main Tabs: English / Other Languages */}
          <div className="main-tabs">
            <button
              type="button"
              className={`main-tab ${mainTab === 'english' ? 'active' : ''}`}
              onClick={() => setMainTab('english')}
            >
              English
            </button>
            <button
              type="button"
              className={`main-tab ${mainTab === 'other' ? 'active' : ''}`}
              onClick={() => setMainTab('other')}
              disabled={!subjectToEdit && step !== 'translations'}
              title={!subjectToEdit && step !== 'translations' ? 'Translate first to enable' : 'Other languages'}
            >
              Other Languages
            </button>
          </div>

          {/* Translating loader */}
          {step === 'translate' && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <h4>Translating…</h4>
              <p>Please wait...</p>
            </div>
          )}

          {/* ENGLISH TAB */}
          {mainTab === 'english' && step !== 'translate' && (
            <form onSubmit={handleSubmit(onInitialTranslate)}>
              {/* Subject Name */}
              <div className="form-grid-container">
                <div style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Subject Name *</label>
                  <input
                    className="form-control"
                    {...register('name', {
                      required: 'Subject Name is required',
                      minLength: { value: 2, message: 'Minimum 2 characters' }
                    })}
                    ref={(e) => {
                      register('name').ref(e);
                      nameInputRef.current = e;
                    }}
                    placeholder="Enter subject name"
                  />
                  {errors.name && <span className="error-message">{errors.name.message}</span>}
                </div>
              </div>

              {/* Image Upload */}
              <div className="form-grid-container">
                <div style={{ gridColumn: 'span 2' }}>
                  {renderImageUpload()}
                  {!subjectToEdit && images.length === 0 && (
                    <span className="error-message">Image is required</span>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="form-grid-container">
                <div>
                  <label className="form-label">Status</label>
                  <div className="form-check style-check d-flex align-items-center gap-8">
                    <input
                      className="form-check-input radius-4 border border-neutral-400"
                      type="checkbox"
                      {...register('active')}
                    />
                    <label className="form-label fw-medium text-primary-light text-sm mb-0">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="border border-danger-600 text-danger-600 px-56 py-10 radius-8"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-yellow px-56 py-12"
                  title={!subjectToEdit && images.length === 0 ? 'Please upload an image first' : 'Translate'}
                  disabled={!subjectToEdit && images.length === 0}
                  style={{ opacity: (!subjectToEdit && images.length === 0) ? 0.5 : 1 }}
                >
                  Translate
                </button>
              </div>
            </form>
          )}

          {/* OTHER LANGUAGES TAB */}
          {mainTab === 'other' && (
            <>
              {/* Language Sub-Tabs (English excluded) */}
              <div className="lang-tabs">
                {otherLanguages.length === 0 ? (
                  <span style={{ opacity: 0.7 }}>No other languages available</span>
                ) : (
                  otherLanguages.map((lang) => (
                    <button
                      key={lang.GoogleLanguageCode}
                      type="button"
                      className={`lang-tab ${selectedLang === lang.GoogleLanguageCode ? 'active' : ''}`}
                      onClick={() => setSelectedLang(lang.GoogleLanguageCode)}
                    >
                      {lang.LanguageName}
                    </button>
                  ))
                )}
              </div>

              {/* Translation Fields */}
              {selectedLang ? (
                <>
                  <div className="form-grid-container">
                    <div style={{ gridColumn: 'span 2' }}>
                      <label className="form-label">Subject Name ({selectedLangName}) *</label>
                      <input
                        className="form-control"
                        value={translations.SubjectName[selectedLang] || ''}
                        onChange={(e) =>
                          setTranslations((prev) => ({
                            ...prev,
                            SubjectName: { ...prev.SubjectName, [selectedLang]: e.target.value }
                          }))
                        }
                        placeholder={`Enter subject name in ${selectedLangName}`}
                      />
                      {!translations.SubjectName[selectedLang]?.trim() && (
                        <span className="error-message">Subject name is required</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ opacity: 0.7, marginBottom: 12 }}>Select a language</div>
              )}

              {/* Buttons */}
              <div className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="border border-danger-600 text-danger-600 px-56 py-10 radius-8"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-yellow px-56 py-12"
                  onClick={onFinalSave}
                  disabled={!isTranslationsValid()}
                  title={!isTranslationsValid() ? "Please fill all fields for all languages" : ""}
                  style={{ opacity: !isTranslationsValid() ? 0.5 : 1 }}
                >
                  Save
                </button>
              </div>
            </>
          )}
        </div>
      </Drawer>
    </>
  );
}